// src/components/alertesCritiques/progressionAlertes.js
// La progression du jour du panneau latéral des alertes critiques : ce que
// l'agent a traité aujourd'hui, et ce qui lui reste.
//
// Module PUR : ni React, ni import, ni accès direct au stockage (on lui passe
// l'objet de stockage). Il se teste avec node, hors du dépôt
// (scratchpad/panneau-lateral/progression.test.mjs).
//
// Une ligne se reconnaît par `detecteur|cle` (cleLigne). La progression
// garde, par ligne, un instantané (titre et gravité du groupe, libellé,
// précision, rang) et un état :
//   - reste      : à traiter ici (corriger, valider, constater, hors
//                  corrections d'une autre année, hors campagne et hors
//                  surveillance) ;
//   - envoyee    : une ligne à traiter devenue attendre_validation (la
//                  correction attend sa validation). Elle COMPTE comme
//                  traitée (décision de Daniel du 16/10/2026) ;
//   - constatee  : un constat confirmé depuis un panneau ;
//   - reglee     : vue à traiter, puis disparue de la réponse, son détecteur
//                  étant ouvert et sans erreur (mot neutre : un collègue a pu
//                  la régler) ;
//   - sortie     : vue à traiter, puis restée dans la réponse sans l'être ici
//                  ('corriger' ou 'constater' devenue 'information' : frais
//                  encaissé, famille devenue distante), ou son détecteur ne
//                  figure plus dans detecteurs_ouverts (droit retiré,
//                  interrupteur coupé). Retirée du total, jamais « Réglée ».
// Un détecteur dans `erreurs` ne change rien à ses lignes. Un serveur sans
// `detecteurs_ouverts` (avant le 16/10/2026) : la garde « détecteur fermé »
// n'est pas appliquée, sinon plus rien ne serait jamais « réglé ».
//
// Comptes : traitées = réglées + constatées + envoyées ; restent = ce qui est
// à traiter dans la dernière réponse ; total = traitées + restent.
// Une ligne encore « à traiter » dans les données de l'onglet mais déjà
// traitée dans la progression vient d'un AUTRE onglet (événement storage :
// constat, relecture plus récente) : intégrer une réponse remet toujours ses
// lignes présentes « reste ». Jusqu'à la relecture suivante, l'état le plus
// récent l'emporte : traitée, hors de « restent », sans bouton. Sans cette
// règle, l'en-tête la comptait deux fois (« 2 traitées sur 4, 2 restent »
// pour trois lignes, contre-relecture du 16/10/2026).
//
// « Nouvelle » (finition de Daniel du 16/10/2026) : seulement une ligne
// apparue APRÈS la première lecture du jour. La progression garde, par
// détecteur, l'instant de sa première lecture réussie du jour (`lus`) : ouvert
// (detecteurs_ouverts) ou renvoyé, et absent de `erreurs`. Une ligne vue à la
// première lecture de son détecteur n'est jamais nouvelle : à la première
// lecture de la journée, aucune ne l'est (avant, toutes l'étaient) ; un
// détecteur en échec au matin, ou ouvert en cours de journée (droit accordé),
// n'annonce pas d'un coup toutes ses lignes. Le son suit la même règle
// (`nouvelles` d'integrerReponse) : jamais à la première lecture du jour.
// Serveur sans detecteurs_ouverts : un détecteur sans ligne n'est pas compté
// lu ; sa première ligne, plus tard, n'est pas annoncée.
//
// Portée : la journée civile à Paris. Au plus 200 lignes (PLAFOND_LIGNES) ;
// au-delà, les lignes les plus anciennes qui ne sont plus à traiter partent
// d'abord. Plus de 200 lignes à traiter dans une réponse : la progression est
// « saturée », plus aucune ligne n'est annoncée comme nouvelle (pas de son).
//
// Plusieurs onglets partagent la progression (localStorage) : chaque écriture
// RELIT ce qui est stocké, le FUSIONNE avec la copie en mémoire (par ligne,
// la plus récemment modifiée l'emporte), applique son changement, puis
// ÉCRIT (mettreAJourProgression). Jamais d'écrasement à l'aveugle.
//
// Limite connue (à dire aux agents) : un validateur qui corrige une réduction
// puis valide sa propre demande voit deux lignes pour une anomalie : la ligne
// corrigée passe « Envoyée », et la demande arrive comme une nouvelle ligne à
// valider (le détecteur des demandes n'écarte pas le demandeur).

// Les lignes que le serveur compte dans `a_traiter`.
export const ACTIONS_A_TRAITER = new Set(['corriger', 'valider', 'constater']);

// Un manque à compléter peu à peu (élèves, personnel sans photo) : jamais « à
// traiter ».
export const GRAVITE_CAMPAGNE = 'campagne';
// Un état à surveiller (classes presque pleines, 30/10/2026) : le régime de la
// campagne, jamais « à traiter » ni dans la progression ni dans la pastille.
export const GRAVITE_SURVEILLANCE = 'surveillance';

// Les gravités dont les lignes sont toujours pour information (serveur :
// estPourInformation dans services/alertesCritiques/detecteurs.js).
export const GRAVITES_POUR_INFORMATION = new Set([GRAVITE_CAMPAGNE, GRAVITE_SURVEILLANCE]);

export const PLAFOND_LIGNES = 200;

export const ETATS = Object.freeze({
  RESTE: 'reste',
  ENVOYEE: 'envoyee',
  CONSTATEE: 'constatee',
  REGLEE: 'reglee',
  SORTIE: 'sortie'
});

const ETATS_VALIDES = new Set(Object.values(ETATS));
const TRAITEES = new Set([ETATS.REGLEE, ETATS.CONSTATEE, ETATS.ENVOYEE]);
const VERSION = 1;

/** L'identifiant d'une ligne dans la progression. */
export const cleLigne = (detecteurId, cle) => `${detecteurId}|${cle}`;

// --- Ce qui est à traiter ici --------------------------------------------------

/**
 * Une ligne « corriger » d'une autre année que celle de la session : elle se
 * corrige dans une session ouverte sur son année.
 */
export function estHorsSession(ligne, anneeSession) {
  return ligne?.action === 'corriger'
    && ligne.annee_scolaire_id != null
    && anneeSession != null
    && String(ligne.annee_scolaire_id) !== String(anneeSession);
}

/** La ligne attend un geste de l'agent dans cette session. */
export function estATraiter(detecteur, ligne, anneeSession) {
  return !GRAVITES_POUR_INFORMATION.has(detecteur?.gravite)
    && ACTIONS_A_TRAITER.has(ligne?.action)
    && !estHorsSession(ligne, anneeSession);
}

/** Le nombre de lignes à traiter dans une réponse, pour une année de session. */
export function compterLignesATraiter(donnees, anneeSession) {
  return (donnees?.detecteurs || []).reduce(
    (total, detecteur) => total + (detecteur.lignes || []).filter((ligne) => estATraiter(detecteur, ligne, anneeSession)).length,
    0
  );
}

// --- Le jour et l'heure à Paris --------------------------------------------------

const formateurs = {};

function formateur(nom, options) {
  if (!(nom in formateurs)) {
    try {
      formateurs[nom] = new Intl.DateTimeFormat(options.locale, { timeZone: 'Europe/Paris', ...options.champs });
    } catch {
      formateurs[nom] = null;
    }
  }
  return formateurs[nom];
}

function parties(nom, options, instant) {
  const f = formateur(nom, options);
  if (!f) return null;
  try {
    const resultat = {};
    for (const partie of f.formatToParts(new Date(instant))) resultat[partie.type] = partie.value;
    return resultat;
  } catch {
    return null;
  }
}

const deux = (nombre) => String(nombre).padStart(2, '0');

/** Le jour civil à Paris, « AAAA-MM-JJ ». */
export function jourParis(instant = Date.now()) {
  const p = parties('jour', { locale: 'en-CA', champs: { year: 'numeric', month: '2-digit', day: '2-digit' } }, instant);
  if (p && p.year && p.month && p.day) return `${p.year}-${p.month}-${p.day}`;
  const date = new Date(instant);
  return `${date.getFullYear()}-${deux(date.getMonth() + 1)}-${deux(date.getDate())}`;
}

/** L'heure à Paris, « HH:MM » (pied du panneau : « Lu à 10:42 »). */
export function heureParis(instant) {
  if (typeof instant !== 'number' || !Number.isFinite(instant)) return '';
  const p = parties('heure', { locale: 'fr-FR', champs: { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' } }, instant);
  if (p && p.hour && p.minute) return `${deux(Number(p.hour) % 24)}:${p.minute}`;
  const date = new Date(instant);
  return `${deux(date.getHours())}:${deux(date.getMinutes())}`;
}

// --- La progression ----------------------------------------------------------------

export function progressionVide(jour) {
  return { v: VERSION, jour, lignes: {}, lus: {} };
}

const nombre = (valeur, repli = 0) => (Number.isFinite(Number(valeur)) ? Number(valeur) : repli);

// Les identifiants viennent du serveur : jamais `in`, qui verrait « constructor ».
const possede = (objet, cle) => Object.prototype.hasOwnProperty.call(objet, cle);

/**
 * Les détecteurs lus aujourd'hui : identifiant -> instant de leur première
 * lecture réussie du jour. Les entrées mal formées sont écartées ; une
 * progression d'avant (sans `lus`) en rend un vide.
 */
function normaliserLus(brut) {
  const lus = {};
  if (!brut || typeof brut !== 'object') return lus;
  for (const [id, instant] of Object.entries(brut)) {
    if (id && typeof instant === 'number' && Number.isFinite(instant)) lus[id] = instant;
  }
  return lus;
}

/** Deux relevés `lus` du même jour : par détecteur, la première lecture la plus ancienne. */
function fusionnerLus(a, b) {
  const lus = { ...a };
  for (const [id, instant] of Object.entries(b)) {
    lus[id] = possede(lus, id) ? Math.min(lus[id], instant) : instant;
  }
  return lus;
}

function recordValide(k, record) {
  return record && typeof record === 'object'
    && typeof record.detecteur === 'string' && record.detecteur
    && typeof record.cle === 'string'
    && cleLigne(record.detecteur, record.cle) === k
    && ETATS_VALIDES.has(record.etat);
}

/**
 * La progression lue, pour le jour donné, ou null (autre jour, forme
 * inconnue). Les lignes mal formées sont écartées une à une.
 */
export function normaliserProgression(brute, jour) {
  if (!brute || typeof brute !== 'object' || brute.jour !== jour || !brute.lignes || typeof brute.lignes !== 'object') {
    return null;
  }
  const lignes = {};
  for (const [k, record] of Object.entries(brute.lignes)) {
    if (!recordValide(k, record)) continue;
    lignes[k] = {
      detecteur: record.detecteur,
      cle: record.cle,
      titre: typeof record.titre === 'string' ? record.titre : null,
      gravite: typeof record.gravite === 'string' ? record.gravite : null,
      libelle: typeof record.libelle === 'string' ? record.libelle : null,
      precision: typeof record.precision === 'string' ? record.precision : null,
      etat: record.etat,
      rang: nombre(record.rang),
      n: nombre(record.n),
      vueLe: nombre(record.vueLe),
      majLe: nombre(record.majLe)
    };
  }
  return { v: VERSION, jour, lignes, lus: normaliserLus(brute.lus) };
}

/**
 * Au plus PLAFOND_LIGNES lignes. Partent d'abord les lignes qui ne sont plus
 * à traiter, absentes de la réponse en cours, les plus anciennement
 * modifiées.
 * @param {Set<string>|null} presentes - les lignes de la réponse en cours
 */
export function plafonner(progression, presentes = null) {
  const entrees = Object.entries(progression.lignes);
  if (entrees.length <= PLAFOND_LIGNES) return progression;
  const priorite = ([k, record]) => (record.etat === ETATS.RESTE ? 2 : 0) + (presentes && presentes.has(k) ? 1 : 0);
  const gardees = entrees
    .map((entree, index) => ({ entree, index, priorite: priorite(entree) }))
    .sort((a, b) => (b.priorite - a.priorite)
      || (b.entree[1].majLe - a.entree[1].majLe)
      || (a.index - b.index))
    .slice(0, PLAFOND_LIGNES)
    .sort((a, b) => a.index - b.index);
  const lignes = {};
  for (const { entree: [k, record] } of gardees) lignes[k] = record;
  return { ...progression, lignes };
}

/**
 * Fusionne deux copies de la progression du même jour (stockée et en
 * mémoire) : par ligne, la plus récemment modifiée l'emporte ; la première
 * vue reste la plus ancienne.
 */
export function fusionnerProgressions(a, b, jour) {
  const pa = normaliserProgression(a, jour);
  const pb = normaliserProgression(b, jour);
  if (!pa && !pb) return progressionVide(jour);
  if (!pa) return plafonner(pb);
  if (!pb) return plafonner(pa);
  const lignes = { ...pa.lignes };
  for (const [k, rb] of Object.entries(pb.lignes)) {
    const ra = lignes[k];
    if (!ra) {
      lignes[k] = rb;
      continue;
    }
    const choisie = rb.majLe >= ra.majLe ? rb : ra;
    lignes[k] = { ...choisie, vueLe: Math.min(ra.vueLe, rb.vueLe), n: Math.min(ra.n, rb.n) };
  }
  return plafonner({ v: VERSION, jour, lignes, lus: fusionnerLus(pa.lus, pb.lus) });
}

function instantane(detecteur, ligne, rang) {
  return {
    detecteur: detecteur.id,
    cle: ligne.cle,
    titre: typeof detecteur.titre === 'string' ? detecteur.titre : null,
    gravite: typeof detecteur.gravite === 'string' ? detecteur.gravite : null,
    libelle: typeof ligne.libelle === 'string' ? ligne.libelle : null,
    precision: typeof ligne.precision === 'string' ? ligne.precision : null,
    rang
  };
}

/**
 * Intègre une réponse de GET /api/alertes-critiques à la progression du jour.
 * @returns {{ progression: object, nouvelles: string[], sature: boolean }}
 *   `nouvelles` : les lignes à traiter jamais vues aujourd'hui (cleLigne)
 *   dont le détecteur avait déjà été lu plus tôt dans la journée : vide à la
 *   première lecture du jour, et quand la progression est saturée.
 */
export function integrerReponse(progression, reponse, { maintenant = Date.now(), anneeSession = null } = {}) {
  const jour = jourParis(maintenant);
  const base = normaliserProgression(progression, jour) || progressionVide(jour);
  const lignes = { ...base.lignes };
  if (!reponse || !Array.isArray(reponse.detecteurs)) return { progression: { ...base, lignes }, nouvelles: [], sature: false };

  const ouverts = Array.isArray(reponse.detecteurs_ouverts)
    ? new Set(reponse.detecteurs_ouverts.map((d) => (d && typeof d === 'object' ? d.id : d)).filter(Boolean))
    : null;
  const enErreur = new Set(Array.isArray(reponse.erreurs) ? reponse.erreurs : []);
  // Lus avant cette lecture : seules leurs lignes jamais vues sont nouvelles.
  const lusAvant = base.lus || {};
  const lus = { ...lusAvant };
  const marquerLu = (id) => {
    if (id && !enErreur.has(id) && !possede(lus, id)) lus[id] = maintenant;
  };
  if (ouverts) ouverts.forEach(marquerLu);
  for (const detecteur of reponse.detecteurs) marquerLu(detecteur && detecteur.id);
  const presentes = new Set();
  const nouvelles = [];
  let prochainN = Object.values(lignes).reduce((max, record) => Math.max(max, record.n), 0);
  let aTraiter = 0;

  for (const detecteur of reponse.detecteurs) {
    if (!detecteur || !detecteur.id) continue;
    const lignesDuDetecteur = detecteur.lignes || [];
    for (let rang = 0; rang < lignesDuDetecteur.length; rang += 1) {
      const ligne = lignesDuDetecteur[rang];
      if (!ligne || typeof ligne.cle !== 'string') continue;
      const k = cleLigne(detecteur.id, ligne.cle);
      presentes.add(k);
      const record = lignes[k];
      const vue = instantane(detecteur, ligne, rang);
      if (estATraiter(detecteur, ligne, anneeSession)) {
        aTraiter += 1;
        if (!record) {
          prochainN += 1;
          lignes[k] = { ...vue, etat: ETATS.RESTE, n: prochainN, vueLe: maintenant, majLe: maintenant };
          if (possede(lusAvant, detecteur.id)) nouvelles.push(k);
        } else {
          lignes[k] = { ...record, ...vue, etat: ETATS.RESTE, majLe: record.etat === ETATS.RESTE ? record.majLe : maintenant };
        }
        continue;
      }
      if (!record) continue; // jamais à traiter ici aujourd'hui : rien à suivre
      let etat = record.etat;
      if (ligne.action === 'attendre_validation') {
        if (etat === ETATS.RESTE || etat === ETATS.SORTIE) etat = ETATS.ENVOYEE;
      } else if (etat === ETATS.RESTE) {
        // Toujours là, mais plus à traiter ici (information, autre année, campagne).
        etat = ETATS.SORTIE;
      }
      lignes[k] = { ...record, ...vue, etat, majLe: etat === record.etat ? record.majLe : maintenant };
    }
  }

  for (const [k, record] of Object.entries(lignes)) {
    if (presentes.has(k)) continue;
    // Vérification en échec : rien ne change pour ses lignes.
    if (enErreur.has(record.detecteur)) continue;
    if (ouverts && !ouverts.has(record.detecteur)) {
      // Détecteur fermé (droit retiré, interrupteur coupé) : jamais « Réglée ».
      if (record.etat === ETATS.RESTE) lignes[k] = { ...record, etat: ETATS.SORTIE, majLe: maintenant };
      continue;
    }
    if (record.etat === ETATS.RESTE || record.etat === ETATS.ENVOYEE) {
      lignes[k] = { ...record, etat: ETATS.REGLEE, majLe: maintenant };
    }
  }

  const sature = aTraiter > PLAFOND_LIGNES;
  return {
    progression: plafonner({ v: VERSION, jour, lignes, lus }, presentes),
    nouvelles: sature ? [] : nouvelles,
    sature
  };
}

/**
 * Un constat confirmé (ou déjà fait ailleurs) : la ligne passe « Constatée ».
 * @param {{ detecteur: object, ligne: object, rang?: number, maintenant?: number }} quoi
 */
export function marquerConstatee(progression, { detecteur, ligne, rang = 0, maintenant = Date.now() } = {}) {
  const jour = jourParis(maintenant);
  const base = normaliserProgression(progression, jour) || progressionVide(jour);
  if (!detecteur || !detecteur.id || !ligne || typeof ligne.cle !== 'string') return base;
  const k = cleLigne(detecteur.id, ligne.cle);
  const record = base.lignes[k];
  const n = record ? record.n : Object.values(base.lignes).reduce((max, r) => Math.max(max, r.n), 0) + 1;
  const lignes = {
    ...base.lignes,
    [k]: {
      ...(record || { vueLe: maintenant }),
      ...instantane(detecteur, ligne, record ? record.rang : rang),
      etat: ETATS.CONSTATEE,
      n,
      majLe: maintenant
    }
  };
  return plafonner({ v: VERSION, jour, lignes, lus: base.lus || {} });
}

/** Traitée aujourd'hui (réglée, constatée, envoyée), d'après la progression. */
const estTraitee = (record) => Boolean(record) && TRAITEES.has(record.etat);

/**
 * Les lignes à traiter des données, moins celles qu'un autre onglet a déjà
 * traitées depuis leur lecture (voir « Comptes » en tête du module).
 */
function compterRestantes(donnees, records, anneeSession) {
  return (donnees?.detecteurs || []).reduce((total, detecteur) => total + (detecteur?.lignes || []).filter(
    (ligne) => estATraiter(detecteur, ligne, anneeSession)
      && !(detecteur.id && typeof ligne?.cle === 'string' && estTraitee(records[cleLigne(detecteur.id, ligne.cle)]))
  ).length, 0);
}

/**
 * Les comptes de l'en-tête du panneau latéral.
 * @returns {{ traitees, reglees, constatees, envoyees, restent, total, toutTraite, aucune }}
 *   `restent` et `total` valent null tant qu'aucune réponse n'est connue.
 */
export function resumerAvancement(progression, donnees, anneeSession) {
  let reglees = 0;
  let constatees = 0;
  let envoyees = 0;
  for (const record of Object.values(progression?.lignes || {})) {
    if (record.etat === ETATS.REGLEE) reglees += 1;
    else if (record.etat === ETATS.CONSTATEE) constatees += 1;
    else if (record.etat === ETATS.ENVOYEE) envoyees += 1;
  }
  const traitees = reglees + constatees + envoyees;
  const restent = donnees ? compterRestantes(donnees, progression?.lignes || {}, anneeSession) : null;
  return {
    traitees,
    reglees,
    constatees,
    envoyees,
    restent,
    total: restent === null ? null : traitees + restent,
    toutTraite: restent === 0 && traitees > 0,
    aucune: restent === 0 && traitees === 0
  };
}

const STATUT_DE_L_ETAT = {
  [ETATS.REGLEE]: 'reglee',
  [ETATS.CONSTATEE]: 'constatee',
  [ETATS.ENVOYEE]: 'envoyee'
};

/**
 * Les groupes à afficher dans le panneau latéral : les lignes de la réponse
 * dans l'ordre du serveur, et à leur place les lignes traitées aujourd'hui
 * qui en sont sorties (réglées, constatées), grâce à leur instantané.
 *
 * Statut d'une ligne :
 *   'a_traiter'   à traiter ici ;
 *   'en_cours'    à traiter, partie en correction (« Corriger » cliqué) ;
 *   'envoyee'     correction en attente de validation, traitée aujourd'hui ;
 *   'reglee'      traitée : disparue de la réponse ;
 *   'constatee'   traitée : constat confirmé ;
 *   (une ligne encore à traiter dans les données, déjà traitée par un autre
 *   onglet depuis leur lecture, prend l'état de la progression) ;
 *   'information' tout le reste (information, autre année, campagne,
 *                 attente de validation d'avant aujourd'hui) : affichée comme
 *                 dans le panneau central.
 * `traitee` : vrai pour 'envoyee', 'reglee', 'constatee' (ligne grisée,
 * repliée, sans bouton). `nouvelle` : ligne à traiter vue pour la première
 * fois depuis `depuis` (l'ouverture du panneau), et après la première lecture
 * du jour de son détecteur (voir « Nouvelle » en tête du module).
 * Groupe `traite` : au moins une ligne traitée, plus aucune à traiter ni en
 * cours, et aucune ligne de son détecteur encore « reste » dans la progression
 * (une vérification en échec garde ses lignes « reste » sans les renvoyer :
 * on ne sait rien d'elles, le groupe garde son ton) ; le panneau lui retire
 * alors son ton de gravité.
 *
 * @returns {Array<{ id, titre, description, gravite, lien, total, traite, lignes:
 *   Array<{ k, cle, detecteurId, ligne, statut, traitee, nouvelle }> }>}
 */
export function composerPanneau(donnees, progression, { enCours = null, depuis = null, anneeSession = null } = {}) {
  const records = progression?.lignes || {};
  const lus = progression?.lus || {};
  const groupes = new Map();
  const groupe = (id, modele, ordre) => {
    if (!groupes.has(id)) {
      groupes.set(id, {
        id,
        titre: modele.titre || null,
        description: modele.description || null,
        gravite: modele.gravite || null,
        lien: modele.lien || null,
        total: Number.isFinite(Number(modele.total)) ? Number(modele.total) : 0,
        lignes: [],
        ordre
      });
    }
    return groupes.get(id);
  };
  const presentes = new Set();
  // Les détecteurs qui ont encore une ligne « reste » (affichée ou non).
  const avecReste = new Set();
  for (const record of Object.values(records)) {
    if (record.etat === ETATS.RESTE) avecReste.add(record.detecteur);
  }
  // Vue depuis l'ouverture, et pas à la première lecture du jour de son détecteur.
  const nouvelleDepuis = (record) => Boolean(record) && depuis !== null && record.vueLe >= depuis
    && possede(lus, record.detecteur) && record.vueLe > lus[record.detecteur];

  (donnees?.detecteurs || []).forEach((detecteur, indexGroupe) => {
    if (!detecteur || !detecteur.id) return;
    const g = groupe(detecteur.id, detecteur, [0, indexGroupe]);
    (detecteur.lignes || []).forEach((ligne, rang) => {
      if (!ligne || typeof ligne.cle !== 'string') return;
      const k = cleLigne(detecteur.id, ligne.cle);
      presentes.add(k);
      const record = records[k];
      let statut = 'information';
      if (estATraiter(detecteur, ligne, anneeSession)) {
        // Déjà traitée par un autre onglet depuis la lecture de ces données.
        if (estTraitee(record)) statut = STATUT_DE_L_ETAT[record.etat];
        else statut = enCours && enCours.has(k) ? 'en_cours' : 'a_traiter';
      } else if (ligne.action === 'attendre_validation' && record && record.etat === ETATS.ENVOYEE) statut = 'envoyee';
      g.lignes.push({
        k,
        cle: ligne.cle,
        detecteurId: detecteur.id,
        ligne,
        statut,
        traitee: statut === 'envoyee' || statut === 'reglee' || statut === 'constatee',
        nouvelle: (statut === 'a_traiter' || statut === 'en_cours') && nouvelleDepuis(record),
        tri: [rang, 1, record ? record.n : 0]
      });
    });
  });

  for (const [k, record] of Object.entries(records)) {
    if (presentes.has(k) || !TRAITEES.has(record.etat)) continue;
    const g = groupe(record.detecteur, { titre: record.titre, gravite: record.gravite }, [1, record.n]);
    g.lignes.push({
      k,
      cle: record.cle,
      detecteurId: record.detecteur,
      ligne: { cle: record.cle, libelle: record.libelle, precision: record.precision, action: null },
      statut: STATUT_DE_L_ETAT[record.etat],
      traitee: true,
      nouvelle: false,
      // Avant la ligne courante de même rang : elle était là avant elle.
      tri: [record.rang, 0, record.n]
    });
  }

  const compare = (a, b) => (a[0] - b[0]) || (a[1] - b[1]) || (a[2] - b[2]);
  return Array.from(groupes.values())
    .sort((a, b) => compare(a.ordre, b.ordre))
    .map(({ ordre, lignes, ...reste }) => ({
      ...reste,
      traite: !avecReste.has(reste.id)
        && lignes.some((ligne) => ligne.traitee)
        && !lignes.some((ligne) => ligne.statut === 'a_traiter' || ligne.statut === 'en_cours'),
      lignes: lignes.sort((a, b) => compare(a.tri, b.tri)).map(({ tri, ...ligne }) => ligne)
    }));
}

// --- Stockage (injecté : localStorage dans le navigateur) ----------------------------

/** La progression stockée pour ce jour, ou null. Ne lève jamais. */
export function lireProgression(stockage, cle, jour) {
  if (!stockage || !cle) return null;
  try {
    const brut = stockage.getItem(cle);
    return brut ? normaliserProgression(JSON.parse(brut), jour) : null;
  } catch {
    return null;
  }
}

/** Écrit la progression ; rend false si le stockage refuse. Ne lève jamais. */
export function ecrireProgression(stockage, cle, progression) {
  if (!stockage || !cle) return false;
  try {
    stockage.setItem(cle, JSON.stringify(progression));
    return true;
  } catch {
    return false;
  }
}

/**
 * Relire, fusionner, écrire : la progression stockée (peut-être écrite par un
 * autre onglet) est fusionnée avec la copie en mémoire, `transformer` y
 * applique le changement, et le résultat est écrit.
 * @param {{ stockage, cle, memoire, maintenant, transformer: (base) => ({ progression, ...autres }) }} options
 * @returns {{ progression: object, ... }} ce que rend `transformer`, avec la progression écrite
 */
export function mettreAJourProgression({ stockage, cle, memoire = null, maintenant = Date.now(), transformer = null } = {}) {
  const jour = jourParis(maintenant);
  const base = fusionnerProgressions(lireProgression(stockage, cle, jour), memoire, jour);
  const resultat = typeof transformer === 'function' ? transformer(base) : null;
  const progression = resultat && resultat.progression ? resultat.progression : base;
  ecrireProgression(stockage, cle, progression);
  return { ...(resultat || {}), progression };
}
